// PackageCard — accordion variant (Feb 2026 release-check refactor).
//
// Why rewrite: Kaily asked for cards that collapse by default so clients can
// scan "name / price / one-liner" quickly on mobile, then tap "View details"
// to see the full brief. All extra fees (travel, parking, tolls, 2nd location)
// moved to a single global "Additional costs" panel below the grid — we
// no longer repeat them inside every card.
//
// Shape of the data (`pkg`) is unchanged; what the card SHOWS is the only
// difference. The parent (Services.jsx > PricingGroup) controls which card
// is expanded via `expanded` + `onToggle` — only one is open at a time.

import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, X, ArrowRight, Clock, MapPin, RefreshCw, FileText, Plus, Minus } from "lucide-react";
import { fadeUp } from "@/components/offer/parts";

const L = {
    en: {
        includes: "What's included",
        excludes: "Not included",
        depends: "Final quotation depends on",
        conditions: "Pricing conditions",
        duration: "Shoot duration",
        locations: "Locations",
        revisions: "Revisions",
        from: "From",
        viewDetails: "View details",
        hide: "Hide details",
        enquire: "Enquire",
        bookDeposit: (d) => `Book · 50% deposit RM ${d}`,
        quote: "Request a quotation",
    },
    cn: {
        includes: "包含",
        excludes: "不包含",
        depends: "最终报价取决于",
        conditions: "价格条件",
        duration: "拍摄时长",
        locations: "地点",
        revisions: "修改",
        from: "起",
        viewDetails: "查看详情",
        hide: "收起",
        enquire: "询价",
        bookDeposit: (d) => `预订 · 50% 定金 RM ${d}`,
        quote: "索取报价",
    },
};

function MetaRow({ icon: Icon, label, value }) {
    if (!value) return null;
    return (
        <div className="flex items-start gap-2.5">
            <Icon className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-cafe-olive" strokeWidth={1.8} />
            <div className="text-[12.5px] leading-snug">
                <span className="font-semibold text-cafe-espresso">{label}: </span>
                <span className="text-cafe-mocha">{value}</span>
            </div>
        </div>
    );
}

function BulletList({ items, icon: Icon, iconClass = "text-cafe-olive", textClass = "text-cafe-mocha" }) {
    return (
        <ul className="space-y-1.5">
            {items.map((b, j) => (
                <li key={j} className={`flex items-start gap-2 text-[13px] leading-snug ${textClass}`}>
                    <Icon className={`h-3.5 w-3.5 flex-shrink-0 mt-0.5 ${iconClass}`} strokeWidth={2.2} />
                    <span>{b}</span>
                </li>
            ))}
        </ul>
    );
}

export default function PackageCard({
    pkg: t, idx, lang,
    expanded = false, onToggle,
    onBook, onQuote, quoteHref, chatHref,
}) {
    const s = L[lang] || L.en;
    const isQuote = t.booking_mode === "quote";
    const deposit = Math.floor((t.price_rm || 0) / 2);

    return (
        <motion.article
            {...fadeUp}
            transition={{ duration: 0.45, delay: (idx % 3) * 0.04 }}
            data-testid={`services-tier-${t.id}`}
            data-expanded={expanded}
            className={[
                "group relative flex flex-col w-full sm:w-[calc(50%-0.625rem)] lg:w-[calc(33.333%-1.167rem)]",
                "rounded-2xl p-5 md:p-6 bg-cafe-cream text-cafe-espresso",
                "border transition-all duration-300",
                expanded
                    ? "border-cafe-olive shadow-[0_20px_50px_-20px_rgba(26,15,10,0.25)]"
                    : "border-cafe-espresso/10 hover:border-cafe-olive/40",
            ].join(" ")}
        >
            {/* Collapsed row: name · price · one-liner · toggle · CTA */}
            <div className="flex items-start justify-between gap-3 mb-2">
                <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase font-medium text-cafe-olive">
                    <span aria-hidden className="h-px w-4 bg-cafe-olive/60" />
                    <span className="tabular-nums">{String(idx + 1).padStart(2, "0")}</span>
                </span>
            </div>

            <h3
                className="font-serif text-[1.4rem] md:text-[1.6rem] tracking-[-0.02em] leading-[1.08] mb-2 text-cafe-espresso"
                data-testid={`services-tier-name-${t.id}`}
            >
                {t.name}
            </h3>

            <div
                className="font-serif text-3xl md:text-[2rem] tracking-[-0.025em] leading-none text-cafe-espresso mb-2"
                data-testid={`services-tier-price-${t.id}`}
            >
                {t.price_prefix && lang !== "cn" && <span className="text-base md:text-lg mr-1.5 align-middle text-cafe-olive">{s.from}</span>}
                {t.price}
                {t.price_prefix && lang === "cn" && <span className="text-base md:text-lg ml-1 align-middle text-cafe-olive">{s.from}</span>}
            </div>

            {t.note && (
                <p
                    className="text-[13px] leading-snug text-cafe-mocha mb-4"
                    data-testid={`services-tier-desc-${t.id}`}
                >
                    {t.note}
                </p>
            )}

            {/* View details toggle */}
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={expanded}
                data-testid={`services-tier-toggle-${t.id}`}
                className="inline-flex items-center gap-1.5 text-[12px] uppercase tracking-[0.2em] font-medium text-cafe-olive hover:text-cafe-espresso transition-colors self-start"
            >
                {expanded ? s.hide : s.viewDetails}
                {expanded ? <Minus className="h-3.5 w-3.5" strokeWidth={2} /> : <Plus className="h-3.5 w-3.5" strokeWidth={2} />}
            </button>

            {/* Expanded details */}
            <AnimatePresence initial={false}>
                {expanded && (
                    <motion.div
                        key="details"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                        data-testid={`services-tier-details-${t.id}`}
                    >
                        <div className="pt-5 mt-5 border-t border-cafe-espresso/10 space-y-5">
                            {/* Meta facts */}
                            <div className="space-y-2.5">
                                <MetaRow icon={Clock} label={s.duration} value={t.duration} />
                                <MetaRow icon={MapPin} label={s.locations} value={t.locations} />
                                <MetaRow icon={RefreshCw} label={s.revisions} value={t.revisions} />
                            </div>

                            {t.bullets.length > 0 && (
                                <div>
                                    <div className="text-[10px] tracking-[0.24em] uppercase font-bold mb-2.5 text-cafe-olive">{s.includes}</div>
                                    <BulletList items={t.bullets} icon={Check} />
                                </div>
                            )}

                            {t.excludes.length > 0 && (
                                <div data-testid={`services-tier-excludes-${t.id}`}>
                                    <div className="text-[10px] tracking-[0.24em] uppercase font-bold mb-2.5 text-cafe-olive">{s.excludes}</div>
                                    <BulletList items={t.excludes} icon={X} iconClass="text-cafe-mocha/60" textClass="text-cafe-mocha/80" />
                                </div>
                            )}

                            {t.quote_depends.length > 0 && (
                                <div>
                                    <div className="text-[10px] tracking-[0.24em] uppercase font-bold mb-2.5 text-cafe-olive">{s.depends}</div>
                                    <ul className="list-disc pl-4 space-y-1 text-[13px] leading-snug text-cafe-mocha">
                                        {t.quote_depends.map((b, j) => <li key={j}>{b}</li>)}
                                    </ul>
                                </div>
                            )}

                            {t.conditions.length > 0 && (
                                <div>
                                    <div className="text-[10px] tracking-[0.24em] uppercase font-bold mb-2.5 text-cafe-olive">{s.conditions}</div>
                                    <BulletList items={t.conditions} icon={FileText} iconClass="text-cafe-olive/70" />
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* CTA — always clickable, collapsed or expanded */}
            <div className="mt-5 pt-5 border-t border-cafe-espresso/10 flex flex-col gap-2">
                {isQuote ? (
                    <button
                        type="button"
                        onClick={() => onQuote(t)}
                        data-testid={`services-tier-cta-${t.id}`}
                        className="inline-flex items-center justify-center gap-2 w-full px-5 py-3 rounded-full text-[13px] font-semibold tracking-wide bg-cafe-espresso text-cafe-cream hover:bg-cafe-olive transition-colors"
                    >
                        {s.quote} <ArrowRight className="h-4 w-4" strokeWidth={2} />
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={() => onBook(t)}
                        disabled={!t.price_rm}
                        data-testid={`services-tier-cta-${t.id}`}
                        className="inline-flex items-center justify-center gap-2 w-full px-5 py-3 rounded-full text-[13px] font-semibold tracking-wide bg-cafe-espresso text-cafe-cream hover:bg-cafe-olive transition-colors disabled:opacity-40"
                    >
                        {t.price_rm ? s.bookDeposit(deposit) : s.enquire} <ArrowRight className="h-4 w-4" strokeWidth={2} />
                    </button>
                )}
                <a
                    href={isQuote ? quoteHref : chatHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`services-tier-chat-${t.id}`}
                    className="inline-flex items-center justify-center w-full px-5 py-2 rounded-full text-[11px] font-medium tracking-[0.18em] uppercase text-cafe-mocha hover:text-cafe-espresso transition-colors"
                >
                    {s.enquire} · WhatsApp
                </a>
            </div>
        </motion.article>
    );
}
